import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Text } from 'v3/components';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Spinner from 'react-bootstrap/Spinner';
import { cooperplaceApi } from 'services/api';
import moment from 'moment';
import CreateChecklistWithLink from './CreateChecklistWithLink';
import ListModal from './ListModal';

function safeStr(v) {
  if (v === null || v === undefined) return '';
  return String(v);
}

function formatDate(value) {
  if (!value) return '—';
  const m = moment(value);
  return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : '—';
}

function normalizeGroupKey(group) {
  const slug = safeStr(group?.slug).trim();
  if (slug) return slug.toLowerCase();

  const name = safeStr(group?.name).trim();
  if (!name) return '';

  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .trim();
}

function Backdrop({ children, onClose }) {
  return (
    <div
      onMouseDown={event => {
        if (event.target === event.currentTarget && onClose) onClose();
      }}
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        role="presentation"
        style={{
          width: '100%',
          maxWidth: 900,
          maxHeight: '90vh',
          overflow: 'hidden',
          borderRadius: 16,
          background: '#FFFFFF',
          boxShadow: '0 20px 40px rgba(15,23,42,0.25)',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function DetailLoadingRow() {
  return (
    <div
      className="d-flex justify-content-center align-items-center py-4"
      role="status"
      aria-busy="true"
      style={{ minHeight: 220 }}
    >
      <Spinner
        animation="border"
        role="status"
        className="mr-3"
        style={{ color: '#9aa4ae' }}
      />
      <Text type="body" style={{ color: '#9aa4ae', fontWeight: 600 }}>
        Carregando
      </Text>
    </div>
  );
}

function ModalLoadingOverlay({ label = 'Gerando link...' }) {
  return (
    <div
      role="status"
      aria-busy="true"
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(255,255,255,0.72)',
        zIndex: 30,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Spinner
          animation="border"
          role="status"
          style={{ color: '#9aa4ae' }}
        />
        <Text type="body" style={{ color: '#111827', fontWeight: 600 }}>
          {label}
        </Text>
      </div>
    </div>
  );
}

function normalizeModel(raw) {
  const groups = Array.isArray(raw?.checklistGroups) ? raw.checklistGroups : [];

  const normalizedGroups = groups
    .map(g => {
      const label = safeStr(g?.name || g?.groupName || g?.label).trim();
      const key = normalizeGroupKey(g);
      return { id: g?.id, key, label, raw: g };
    })
    .filter(g => g.key && g.label);

  const seen = new Set();
  const uniqueGroups = normalizedGroups.filter(g => {
    if (seen.has(g.key)) return false;
    seen.add(g.key);
    return true;
  });

  const groupsKeys = uniqueGroups.map(g => g.key);
  const groupLabel = uniqueGroups.length
    ? uniqueGroups.map(g => g.label).join(', ')
    : '';

  const itemsCount = Array.isArray(raw?.checklistsQuestions)
    ? raw.checklistsQuestions.length
    : Array.isArray(raw?.questions)
    ? raw.questions.length
    : 0;

  const createdRaw = raw?.createdAt || '';

  return {
    id: raw?.id,
    title: raw?.name || '',
    description: raw?.description || '',
    group: groupLabel,
    groups: uniqueGroups,
    groupsKeys,
    items: itemsCount,
    createdAt: formatDate(createdRaw),
    raw,
  };
}

function normalizeCreatedRequest(raw) {
  const payload = raw?.data || raw;

  const id = payload?.id;
  const link = payload?.link || null;
  const linkUrl = link?.url || '';

  return { id, link, linkUrl, raw: payload };
}

export default function RequestChecklistModal({ open, onClose, onCreated }) {
  const [step, setStep] = useState('select');
  const [selectedModel, setSelectedModel] = useState(null);
  const [groupFilter, setGroupFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expireDays, setExpireDays] = useState(null);
  const [linkName, setLinkName] = useState('');
  const [linkNameTouched, setLinkNameTouched] = useState(false);
  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [didInitialLoad, setDidInitialLoad] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;

    setDidInitialLoad(false);
    setLoadingModels(false);
    setModels([]);
    setGroupFilter('all');
    setSearch('');
    setStep('select');
    setSelectedModel(null);
    setExpireDays(7);
    setLinkName('');
    setLinkNameTouched(false);
    setCreating(false);
  }, [open]);

  const loadModels = useCallback(async () => {
    try {
      setLoadingModels(true);

      const { data } = await cooperplaceApi.get(
        '/training-checklist/requests',
        {
          params: {
            checklistName: search?.trim() || undefined,
            page: 1,
            perPage: 50,
          },
        }
      );

      const arr = data?.data || data || [];
      const normalized = Array.isArray(arr) ? arr.map(normalizeModel) : [];
      setModels(normalized);
    } catch (e) {
      setModels([]);
    } finally {
      setLoadingModels(false);
      setDidInitialLoad(true);
    }
  }, [search]);

  useEffect(() => {
    if (!open) return;
    loadModels();
  }, [open, loadModels]);

  const linkNameError =
    linkNameTouched && !linkName.trim()
      ? 'Informe um nome para o checklist do link.'
      : '';

  const groupTabs = useMemo(() => {
    const map = new Map();
    (models || []).forEach(m => {
      (m?.groups || []).forEach(g => {
        if (g?.key && g?.label && !map.has(g.key)) map.set(g.key, g.label);
      });
    });
    const dynamic = Array.from(map.entries()).map(([key, label]) => ({
      key,
      label,
    }));
    return [{ key: 'all', label: 'Todos' }].concat(dynamic);
  }, [models]);

  const selectedGroups = useMemo(() => {
    if (groupFilter === 'all') return [];
    return Array.isArray(groupFilter)
      ? groupFilter.map(String)
      : [String(groupFilter)];
  }, [groupFilter]);

  const filteredModels = useMemo(() => {
    if (!selectedGroups.length) return models;
    return (models || []).filter(m => {
      const keys = Array.isArray(m?.groupsKeys) ? m.groupsKeys.map(String) : [];
      return selectedGroups.some(g => keys.includes(g));
    });
  }, [models, selectedGroups]);

  useEffect(() => {
    if (groupFilter === 'all') return;

    const selected = Array.isArray(groupFilter) ? groupFilter : [groupFilter];
    const validKeys = new Set(groupTabs.map(t => String(t.key)));

    const cleaned = selected.map(String).filter(k => validKeys.has(k));
    if (!cleaned.length) setGroupFilter('all');
    else if (cleaned.length !== selected.length) setGroupFilter(cleaned);
  }, [groupFilter, groupTabs]);

  function resetAndClose() {
    setStep('select');
    setSelectedModel(null);
    setGroupFilter('all');
    setSearch('');
    setExpireDays(7);

    if (onClose) onClose();
  }

  function handleSelectModel(model) {
    setSelectedModel(model);
    setStep('config');
  }

  function handleBack() {
    if (step === 'config') {
      setStep('select');
      setSelectedModel(null);
      return;
    }

    resetAndClose();
  }

  async function createRequest({ withLink, notifyParent = true }) {
    const checklistId = Number(selectedModel?.id ?? selectedModel?.raw?.id);
    if (!checklistId || Number.isNaN(checklistId)) return null;

    const trimmedLinkName = linkName.trim();
    const body = withLink
        ? {
            name: trimmedLinkName,
            link: {
              isAnonymous: false,
              expirationDays: Number(expireDays) || 0,
            },
          }
      : {};

    try {
      setCreating(true);
      const { data } = await cooperplaceApi.post(
        `/training-checklist/requests/${checklistId}`,
        body
      );
      const normalized = normalizeCreatedRequest(data?.data || data || {});
      if (notifyParent && onCreated) onCreated(normalized);
      return normalized;
    } catch (e) {
      return null;
    } finally {
      setCreating(false);
    }
  }

  async function handleGenerateLink() {
    if (!linkName.trim()) {
      setLinkNameTouched(true);
      return;
    }

    const created = await createRequest({
      withLink: true,
      notifyParent: false,
    });
    if (!created) return;

    if (onCreated) onCreated(created);
    resetAndClose();
  }

  function renderHeaderTitle() {
    if (step === 'select') return 'Solicitar Treinamento';
    return selectedModel?.title || 'Treinamento';
  }

  function renderHeaderSubtitle() {
    if (step === 'select')
      return 'Selecione um treinamento para envio por link';
    return 'Configure o link do treinamento';
  }

  const linkBody = (
    <CreateChecklistWithLink
      linkName={linkName}
      setLinkName={setLinkName}
      linkNameTouched={linkNameTouched}
      setLinkNameTouched={setLinkNameTouched}
      linkNameError={linkNameError}
      expireDays={expireDays}
      setExpireDays={setExpireDays}
      onCancel={handleBack}
      onGenerate={handleGenerateLink}
      loading={creating}
    />
  );

  function renderConfigStep() {
    return linkBody;
  }

  if (!open) return null;

  return (
    <Backdrop onClose={resetAndClose}>
      <div style={{ padding: '1.5rem 1.75rem', position: 'relative' }}>
        {creating ? <ModalLoadingOverlay /> : null}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '0.5rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              flex: 1,
              minWidth: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <DescriptionOutlinedIcon
                style={{ fontSize: 20, color: '#1F2937' }}
              />
              <Text type="header" color="dark" style={{ fontWeight: 700 }}>
                {renderHeaderTitle()}
              </Text>
            </div>

            <div
              style={{
                width: '100%',
                height: 1,
                backgroundColor: '#D1D5DB',
                margin: '8px 0',
              }}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <InfoOutlinedIcon style={{ fontSize: 14, color: '#6B7280' }} />
              <Text
                color="gray"
                style={{ fontSize: 10, lineHeight: '14px', margin: 0 }}
              >
                {renderHeaderSubtitle()}
              </Text>
            </div>
          </div>

          <button
            type="button"
            onClick={resetAndClose}
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: 20,
              cursor: 'pointer',
              lineHeight: 1,
              marginLeft: 12,
              color: '#111827',
            }}
            aria-label="Fechar"
            title="Fechar"
          >
            ✕
          </button>
        </div>

        <div style={{ marginTop: '1.5rem' }}>
          {step === 'select' ? (
            !didInitialLoad ? (
              <DetailLoadingRow />
            ) : (
              <ListModal
                models={filteredModels}
                loading={loadingModels}
                groupTabs={groupTabs}
                groupFilter={groupFilter}
                onChangeGroupFilter={v => setGroupFilter(v)}
                search={search}
                onChangeSearch={v => setSearch(v)}
                onSelectModel={handleSelectModel}
                onRefresh={loadModels}
              />
            )
          ) : (
            selectedModel && renderConfigStep()
          )}
        </div>
      </div>
    </Backdrop>
  );
}
